import { BUSINESS_HOURS, TOUCH_SILENCE_MS } from "./config";

/** スナップショット h 区画。プライバシー方針により生時刻でなく「N分前」で丸める */
export interface HealthSection {
  up: number; // 稼働秒
  silent: 0 | 1; // タッチ途絶フラグ
  lastTouchMin: number | null; // 最終タッチのN分前 (タッチ未発生なら null)
  mem?: number; // 使用JSヒープ MB (Chromium系のみ)
}

export const inBusinessHours = (nowMs: number): boolean => {
  const h = new Date(nowMs).getHours();
  return h >= BUSINESS_HOURS.startHour && h < BUSINESS_HOURS.endHour;
};

/** 当日の営業開始時刻 (ローカル time-of-day を BUSINESS_HOURS.startHour に固定) */
const businessStartMs = (nowMs: number): number => {
  const d = new Date(nowMs);
  d.setHours(BUSINESS_HOURS.startHour, 0, 0, 0);
  return d.getTime();
};

/**
 * タッチ途絶判定: 営業時間帯かつ、当日営業開始時刻以降で直近 TOUCH_SILENCE_MS タッチゼロ。
 * 基準時刻を「最終タッチ」と「当日営業開始」の遅い方にクランプすることで、前夜最後の
 * タッチからの経過時間が朝一に持ち越されて偽陽性になるのを防ぐ (未操作でも開店直後は猶予)。
 */
export const isTouchSilent = (nowMs: number, lastInteractionAtMs: number | null): boolean => {
  if (!inBusinessHours(nowMs)) return false;
  const todayStart = businessStartMs(nowMs);
  const baseline =
    lastInteractionAtMs === null ? todayStart : Math.max(lastInteractionAtMs, todayStart);
  return nowMs - baseline >= TOUCH_SILENCE_MS;
};

interface PerformanceWithMemory extends Performance {
  memory?: { usedJSHeapSize: number };
}

const usedHeapMB = (): number | undefined => {
  if (typeof performance === "undefined") return undefined;
  const mem = (performance as PerformanceWithMemory).memory;
  if (!mem || typeof mem.usedJSHeapSize !== "number") return undefined;
  return Math.round(mem.usedJSHeapSize / (1024 * 1024));
};

export const evaluateHealth = (
  nowMs: number,
  startedAtMs: number,
  lastInteractionAtMs: number | null,
): HealthSection => {
  const h: HealthSection = {
    up: Math.round((nowMs - startedAtMs) / 1000),
    silent: isTouchSilent(nowMs, lastInteractionAtMs) ? 1 : 0,
    lastTouchMin:
      lastInteractionAtMs === null
        ? null
        : Math.max(0, Math.round((nowMs - lastInteractionAtMs) / 60000)),
  };
  const mem = usedHeapMB();
  if (mem !== undefined) h.mem = mem;
  return h;
};
